import { Formik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Eye,
  Edit2,
  Trash2,
  Link,
  Plus,
  Briefcase,
  AlertCircle,
  ExternalLink,
} from "react-feather";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  createCompanyApi,
  deleteCompanyApi,
  editCompanyApi,
  listOfCompanyWithPaginationApi,
} from "../../Api/companyApi";
import BaseButton from "../../BaseComponents/BaseButton";
import BaseInput from "../../BaseComponents/BaseInput";
import BaseModal from "../../BaseComponents/BaseModal";
import TableContainer from "../../BaseComponents/BaseTable";
import BaseTooltip from "../../BaseComponents/BaseTooltip";
import { Country, Name, Submit, Update } from "../../Constant";
import { checkStatusCodeSuccess } from "../../Constant/common";
import { truncateText } from "../../Constant/Lead";
import { SrNo } from "../../Constant/Center";
import { useTableQueryParams } from "../../utils/tableUtils";
import {
  Actions,
  AddCompanyLabel,
  Address,
  Company_Address,
  Company_Contact,
  Company_Country,
  Company_Email,
  Company_Name,
  Company_Remarks,
  CompanyAddress,
  CompanyCountry,
  CompanyLabel,
  Contact,
  Contact_Link,
  Contact_No,
  Contactlink,
  Delete,
  DeleteCompany,
  DeleteConfirmation,
  EditCompany,
  Email,
  NewTab,
  Remarks,
  SearchCompany,
} from "../../Constant/Company";
import { emailRegex, InputPlaceHolder, validationMessages } from "../../Constant/validation";
import { ContactNumPlaceholder, EmailPlaceholder } from "../../Constant/Contact";
import ActionDropdown from "../../BaseComponents/BaseActionDropdown";
import { usePageLoader } from "../../_helper/loading/LoadingContext";

const CompanyValidationSchema = Yup.object().shape({
  company_name: Yup.string().required(validationMessages.required(CompanyLabel)),
  company_country: Yup.string().required(validationMessages.required(CompanyCountry)),
  contact_link: Yup.string().url(validationMessages.url(Contactlink.toLowerCase())),
  email: Yup.string().matches(emailRegex, validationMessages.phoneNumber(EmailPlaceholder)),
  contact_no: Yup.string()
    .matches(/^\+\d{7,15}$/, validationMessages.phoneNumber(ContactNumPlaceholder))
    .min(8, validationMessages.minLength(ContactNumPlaceholder, 8))
    .max(16, validationMessages.maxLength(ContactNumPlaceholder, 16)),
});

const EMPTY_FORM = {
  company_name: "",
  company_country: "",
  company_address: "",
  contact_link: "",
  email: "",
  contact_no: "",
  remarks: "",
};

const Company = () => {
  useEffect(() => {
    document.title = CompanyLabel;
  }, []);

  const { setLoading } = usePageLoader();

  /* ── table params state ── */
  const {
    currentPage,
    customPageSize,
    searchValue,
    sortOrder,
    columnName,
    handleSearchChange,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    resetToFirstPage,
  } = useTableQueryParams({ customPageSize: 5, columnName: "id", sortOrder: "desc" });

  const [companyData, setCompanyData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [selectedRemarks, setSelectedRemarks] = useState("");
  const [openActionId, setOpenActionId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitLoader, setSubmitLoader] = useState(false);

  /* ── Fetch ── */
  const fetchCompanyList = useCallback(() => {
    setLoading(true);
    listOfCompanyWithPaginationApi({
      search: searchValue,
      order: [columnName, sortOrder?.toLowerCase()],
      pageSize: customPageSize,
      pageNumber: currentPage,
    })
      .then((response) => {
        if (checkStatusCodeSuccess(response?.statusCode)) {
          setCompanyData(response?.data?.listOfCompanies || []);
          setTotalPages(response?.data?.totalPages || 1);
          setTotalRecords(response?.data?.totalRecordsCount || 0);
          setTotalNumberOfRows(response?.data?.numberOfRows || 0);
        } else {
          setCompanyData([]);
          setTotalRecords(0);
          setTotalPages(1);
          setTotalNumberOfRows(0);
        }
      })
      .catch(() => setCompanyData([]))
      .finally(() => setLoading(false));
  }, [searchValue, columnName, sortOrder, customPageSize, currentPage, setLoading]);

  useEffect(() => {
    fetchCompanyList();
  }, [fetchCompanyList]);

  /* ── Delete ── */
  const handleDeleteCompany = () => {
    if (!companyToDelete) return;
    setDeleteLoader(true);
    deleteCompanyApi(companyToDelete)
      .then((response) => {
        if (checkStatusCodeSuccess(response?.statusCode)) {
          toast.success(response?.message);
          setShowDeleteModal(false);
          resetToFirstPage();
        } else toast.error(response?.message);
      })
      .catch((err) => toast.error(err?.response?.data?.message || err?.message))
      .finally(() => {
        setDeleteLoader(false);
        setCompanyToDelete(null);
      });
  };

  /* ── Submit ── */
  const handleSubmitCompany = (values, resetForm) => {
    setSubmitLoader(true);
    const apiCall = isEditMode
      ? editCompanyApi({ id: selectedId, ...values })
      : createCompanyApi(values);
    apiCall
      .then((response) => {
        if (checkStatusCodeSuccess(response?.statusCode)) {
          toast.success(response?.message);
          setShowAddModal(false);
          resetForm();
          setFormData(EMPTY_FORM);
          setIsEditMode(false);
          setSelectedId(null);
          resetToFirstPage();
        }
      })
      .catch((err) => toast.error(err?.response?.data?.message || err?.message))
      .finally(() => setSubmitLoader(false));
  };

  /* ── Close outside click ── */
  useEffect(() => {
    const h = () => setOpenActionId(null);
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, []);

  /* ── Columns ── */
  const TipCell = (val, max = 28, color = "#6366f1") => {
    const t = truncateText(val, max);
    return val && val.length > max ? (
      <BaseTooltip label={val} position="top" maxWidth={280} color={color}>
        <span style={{ cursor: "default", borderBottom: "1px dashed var(--glass-border)" }}>
          {t}
        </span>
      </BaseTooltip>
    ) : (
      <span>{val || "—"}</span>
    );
  };

  const columns = useMemo(
    () => [
      {
        header: SrNo,
        accessorKey: "serial_number",
        enableColumnFilter: false,
        cell: (cell) => (currentPage - 1) * customPageSize + cell.row.index + 1,
      },
      {
        id: Company_Name,
        header: Name,
        accessorKey: Company_Name,
        enableColumnFilter: false,
        cell: (c) => TipCell(c.row.original?.company_name, 22, "#6366f1"),
      },
      {
        id: Company_Country,
        header: Country,
        accessorKey: Company_Country,
        enableColumnFilter: false,
        cell: (c) => TipCell(c.row.original?.company_country, 20, "#8b5cf6"),
      },
      {
        id: Company_Address,
        header: Address,
        accessorKey: Company_Address,
        enableColumnFilter: false,
        cell: (cell) => {
          const addr = cell.row.original?.company_address;
          const truncated = truncateText(addr, 30);
          return addr && addr.length > 30 ? (
            <BaseTooltip label={addr} position="top" maxWidth={280} color="#6366f1">
              <span style={{ cursor: "default", borderBottom: "1px dashed var(--glass-border)" }}>
                {truncated}
              </span>
            </BaseTooltip>
          ) : (
            <span>{truncated}</span>
          );
        },
      },
      {
        id: Company_Email,
        header: Email,
        accessorKey: Company_Email,
        enableColumnFilter: false,
        cell: (c) => TipCell(c.row.original?.email, 26, "#06b6d4"),
      },
      {
        id: Company_Contact,
        header: Contact,
        accessorKey: Company_Contact,
        enableColumnFilter: false,
        cell: (c) => TipCell(c.row.original?.contact_no, 18, "#10b981"),
      },
      {
        id: Actions.toLowerCase(),
        header: Actions,
        accessorKey: Actions.toLowerCase(),
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const company = row.original;
          const isOpen = openActionId === company.id;
          const actions = [
            {
              label: Remarks,
              icon: Eye,
              color: "#10b981",
              onClick: () => {
                setSelectedRemarks(company?.remarks || "—");
                setShowRemarksModal(true);
                setOpenActionId(null);
              },
            },
            {
              label: Contactlink,
              icon: ExternalLink,
              color: "#06b6d4",
              disabled: !company?.contact_link,
              onClick: () => {
                window.open(company.contact_link, NewTab);
                setOpenActionId(null);
              },
            },
            {
              label: EditCompany,
              icon: Edit2,
              color: "#f59e0b",
              onClick: () => {
                setIsEditMode(true);
                setSelectedId(company.id);
                setFormData({
                  company_name: company.company_name || "",
                  company_country: company.company_country || "",
                  company_address: company.company_address || "",
                  contact_link: company.contact_link || "",
                  email: company.email || "",
                  contact_no: company.contact_no || "",
                  remarks: company.remarks || "",
                });
                setShowAddModal(true);
                setOpenActionId(null);
              },
            },
            {
              label: DeleteCompany,
              icon: Trash2,
              color: "#f43f5e",
              onClick: () => {
                setCompanyToDelete(company.id);
                setShowDeleteModal(true);
                setOpenActionId(null);
              },
            },
          ];
          return (
            <ActionDropdown
              isOpen={isOpen}
              onToggle={() => setOpenActionId(isOpen ? null : company.id)}
              onClose={() => setOpenActionId(null)}
              actions={actions}
            />
          );
        },
      },
    ],
    [openActionId, currentPage, customPageSize],
  );

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* ── Page header ── */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "rgba(99,102,241,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Briefcase size={20} strokeWidth={1.75} style={{ color: "var(--brand-indigo)" }} />
          </div>
          <div>
            <h1
              style={{
                fontSize: "1.375rem",
                fontWeight: 800,
                margin: 0,
                color: "var(--text-primary)",
              }}
            >
              {CompanyLabel}
            </h1>
            <p
              style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}
            >
              {totalRecords > 0 ? `${totalRecords} companies` : "Manage your companies"}
            </p>
          </div>
        </div>
        <BaseButton
          color="primary"
          onClick={() => {
            setFormData(EMPTY_FORM);
            setIsEditMode(false);
            setSelectedId(null);
            setShowAddModal(true);
          }}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <Plus size={15} strokeWidth={2.5} /> {AddCompanyLabel}
        </BaseButton>
      </div>

      {/* ── Table ── */}
      <div
        className="animate-slide-up"
        style={{
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 16,
          overflow: "hidden",
          padding: "16px",
        }}
      >
        {companyData?.length > 0 || searchValue ? (
          <TableContainer
            totalPages={totalPages}
            totalRecords={totalRecords}
            totalNumberOfRows={totalNumberOfRows}
            setCurrentPage={handlePageChange}
            currentPage={currentPage}
            fetchData={handlePageChange}
            customPageSize={customPageSize}
            setCustomPageSize={handlePageSizeChange}
            columns={columns}
            shouldLineBreak={false}
            data={companyData}
            onSearch={handleSearchChange}
            fetchSortingData={handleSortChange}
            SearchPlaceholder={SearchCompany}
            isGlobalFilter={true}
            manualPagination={true}
            manualFiltering={true}
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "60px 20px",
              gap: 12,
            }}
          >
            <AlertCircle size={40} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
            <p style={{ color: "var(--text-muted)", margin: 0 }}>No companies found</p>
          </div>
        )}
      </div>

      {/* ── Add / Edit modal ── */}
      <Formik
        enableReinitialize
        initialValues={formData}
        validationSchema={CompanyValidationSchema}
        onSubmit={(values, { resetForm }) => handleSubmitCompany(values, resetForm)}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, resetForm }) => (
          <BaseModal
            isOpen={showAddModal}
            toggler={() => {
              if (!submitLoader) {
                resetForm();
                setFormData(EMPTY_FORM);
                setIsEditMode(false);
                setSelectedId(null);
                setShowAddModal(false);
              }
            }}
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 9,
                    background: "rgba(99,102,241,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {isEditMode ? (
                    <Edit2 size={14} strokeWidth={2} style={{ color: "var(--brand-indigo)" }} />
                  ) : (
                    <Plus size={14} strokeWidth={2.5} style={{ color: "var(--brand-indigo)" }} />
                  )}
                </div>
                <span>{isEditMode ? EditCompany : AddCompanyLabel}</span>
              </div>
            }
            submitText={isEditMode ? Update : Submit}
            disabled={submitLoader}
            loader={submitLoader}
            submit={handleSubmit}
            size="lg"
          >
            <form>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                  gap: "0 16px",
                }}
              >
                <BaseInput
                  label={CompanyLabel}
                  name={Company_Name}
                  placeholder={InputPlaceHolder(CompanyLabel)}
                  value={values.company_name}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  touched={touched.company_name}
                  error={touched.company_name && errors.company_name}
                  required
                />
                <BaseInput
                  label={CompanyCountry}
                  name={Company_Country}
                  placeholder={InputPlaceHolder(CompanyCountry)}
                  value={values.company_country}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  touched={touched.company_country}
                  error={touched.company_country && errors.company_country}
                  required
                />
                <BaseInput
                  label={Email}
                  name={Company_Email}
                  placeholder={InputPlaceHolder(Email)}
                  value={values.email}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  touched={touched.email}
                  error={touched.email && errors.email}
                />
                <BaseInput
                  type="phone"
                  label={Contact_No}
                  name={Company_Contact}
                  placeholder={InputPlaceHolder(Contact_No)}
                  value={values.contact_no}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  touched={touched.contact_no}
                  error={touched.contact_no && errors.contact_no}
                />
                <BaseInput
                  label={Contactlink}
                  name={Contact_Link}
                  placeholder={InputPlaceHolder(Contactlink)}
                  value={values.contact_link}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  touched={touched.contact_link}
                  error={touched.contact_link && errors.contact_link}
                  icon={<Link size={13} strokeWidth={2} />}
                />
                <BaseInput
                  type="textarea"
                  label={CompanyAddress}
                  name={Company_Address}
                  placeholder={InputPlaceHolder(CompanyAddress)}
                  value={values.company_address}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                />
                <div style={{ gridColumn: "1 / -1" }}>
                  <BaseInput
                    label={Remarks}
                    type="textarea"
                    name={Company_Remarks}
                    placeholder={InputPlaceHolder(Remarks)}
                    value={values.remarks}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                  />
                </div>
              </div>
            </form>
          </BaseModal>
        )}
      </Formik>

      {/* ── Delete modal ── */}
      <BaseModal
        isOpen={showDeleteModal}
        title={DeleteCompany}
        toggler={() => setShowDeleteModal(false)}
        submit={handleDeleteCompany}
        submitText={Delete}
        disabled={deleteLoader}
        loader={deleteLoader}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "4px 0" }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: "rgba(244,63,94,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Trash2 size={16} strokeWidth={2} style={{ color: "#f43f5e" }} />
          </div>
          <p
            style={{
              margin: 0,
              fontSize: "0.9375rem",
              color: "var(--text-primary)",
              lineHeight: 1.5,
            }}
          >
            {DeleteConfirmation}
          </p>
        </div>
      </BaseModal>

      {/* ── Remarks modal ── */}
      <BaseModal
        isOpen={showRemarksModal}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: "rgba(16,185,129,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Eye size={14} strokeWidth={2} style={{ color: "#10b981" }} />
            </div>
            <span>{Remarks}</span>
          </div>
        }
        toggler={() => setShowRemarksModal(false)}
        hideSubmit
      >
        <div
          style={{
            whiteSpace: "pre-wrap",
            fontSize: "0.9375rem",
            color: "var(--text-primary)",
            lineHeight: 1.7,
            padding: "4px 0",
            minHeight: 60,
          }}
        >
          {selectedRemarks || <span style={{ color: "var(--text-muted)" }}>No remarks added.</span>}
        </div>
      </BaseModal>
    </div>
  );
};

export default Company;
